/**
 * M64 Reveal Hero Section
 * Handles color selection, video reveal, and state management
 */

// Redirect color-specific pages to main M64 page
(function() {
  const path = window.location.pathname;
  if (path.includes('/pages/m64-purple') || 
    path.includes('/pages/m64-green') || 
    path.includes('/pages/m64-white') ||
    path.includes('/pages/m64-red')) {
    window.location.replace('/pages/m64');
  }
})();

const M64Reveal = {
  state: {
    colorSelected: false,
    currentColor: null,
    currentVideoColor: null,
    revealPlayed: false,
    autoPlayTriggered: false
  },
  
  elements: {
    hero: null,
    unlitImage: null,
    swatches: null,
    prompt: null,
    floatingCta: null,
    revealVideos: {},
    litImages: {}
  },
  
  init() {
    // Cache DOM elements
    this.elements.hero = document.getElementById('m64-reveal-hero');
    this.elements.unlitImage = document.getElementById('m64-unlit-image');
    this.elements.swatches = document.querySelectorAll('.m64-hero__swatch');
    this.elements.prompt = document.getElementById('m64-prompt');
    this.elements.floatingCta = document.getElementById('m64-floating-cta');
    
    // Cache video and image elements
    for (let i = 1; i <= 4; i++) {
      this.elements.revealVideos[i] = document.getElementById(`reveal-video-${i}`);
      this.elements.litImages[i] = document.getElementById(`lit-image-${i}`);
    }
    
    this.bindEvents();
    this.setupWaitlistObserver();
    this.preloadVideos();
    this.setupScrollTrigger();
  },
  
  setupScrollTrigger() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.state.autoPlayTriggered) {
          this.state.autoPlayTriggered = true;
          // Auto-select white and play its reveal video
          setTimeout(() => {
            this.selectColor('3');
          }, 300); // Small delay to ensure everything is loaded
        }
      });
    }, {
      threshold: 0.3 // Trigger when 30% of hero is visible
    });
    
    if (this.elements.hero) {
      observer.observe(this.elements.hero);
    }
  },
  
  preloadVideos() {
    // Aggressively preload all videos
    Object.values(this.elements.revealVideos).forEach(video => {
      if (video) {
        // Force the browser to start buffering
        video.load();
        
        // Play and immediately pause to force buffering
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            video.pause();
            video.currentTime = 0;
          }).catch(() => {
            // Autoplay was prevented, that's fine
          });
        }
      }
    });
  },
  
  bindEvents() {
    this.elements.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        const color = e.currentTarget.dataset.color;
        this.selectColor(color);
      });
    });
    
    // Smooth scroll for CTA button - scrolls back to top waitlist section
    this.elements.floatingCta.addEventListener('click', () => {
      const waitlistSection = document.getElementById('m64-waitlist-signup');
      if (waitlistSection) {
        waitlistSection.scrollIntoView({ behavior: 'smooth' });
      }
    });
  },
  
  selectColor(color) {
    // Update swatch selection state
    this.elements.swatches.forEach(swatch => {
      swatch.classList.toggle('is-selected', swatch.dataset.color === color);
    });
    
    if (!this.state.revealPlayed) {
      this.playRevealVideo(color);
    } else {
      this.swapToColor(color);
    }
    
    this.state.currentColor = color;
  },
  
  playRevealVideo(color) {
    const video = this.elements.revealVideos[color];
    
    // Disable swatches during video playback
    document.querySelector('.m64-hero__swatches').classList.add('is-disabled');
    
    // Hide unlit image immediately on click
    this.elements.unlitImage.classList.add('is-hidden');
    
    const startPlayback = () => {
      // Reset to first frame
      video.currentTime = 0;
      
      // Show video
      video.classList.add('is-playing');
      
      video.play();
    };
    
    // Check if video is ready
    if (video.readyState >= 3) {
      startPlayback();
    } else {
      video.addEventListener('canplay', () => {
        startPlayback();
      }, { once: true });
      video.load();
    }
    
    video.addEventListener('ended', () => {
      // Hide the video and show the static lit image instead
      video.classList.remove('is-playing');
      this.elements.litImages[color].classList.add('is-active');
      
      this.state.currentVideoColor = color;
      this.state.revealPlayed = true;
      this.state.colorSelected = true;
      
      document.querySelector('.m64-hero__swatches').classList.remove('is-disabled');
      
      this.revealContent();
      this.dispatchColorEvent(color);
    }, { once: true });
  },

  swapToColor(color) {
    if (this.state.currentVideoColor === color) {
      return;
    }
    
    // Use static images for all color switches (both mobile and desktop)
    Object.values(this.elements.revealVideos).forEach(v => {
      v.classList.remove('is-playing');
    });
    
    Object.values(this.elements.litImages).forEach(img => {
      img.classList.remove('is-active');
    });
    
    this.elements.litImages[color].classList.add('is-active');
    this.state.currentVideoColor = color;
    
    this.dispatchColorEvent(color);
  },
  
  revealContent() {
    document.body.classList.add('m64-color-selected');
  },
  
  showFloatingCta() {
    this.elements.floatingCta.classList.add('is-visible');
  },
  
  setupWaitlistObserver() {
  const waitlistSection = document.getElementById('m64-waitlist-signup');
  const footer = document.querySelector('.footer-group');
  
  const checkVisibility = () => {
    // Check if we've scrolled past the waitlist section
    const waitlistRect = waitlistSection ? waitlistSection.getBoundingClientRect() : null;
    const waitlistPassed = waitlistRect && waitlistRect.bottom < 0; // Bottom of waitlist is above viewport
    
    // Check if footer is visible
    const footerVisible = footer && 
      footer.getBoundingClientRect().top < window.innerHeight && 
      footer.getBoundingClientRect().bottom > 0;
    
    // Show CTA when scrolled past waitlist (and footer not visible)
    // Hide CTA when still on waitlist OR footer is visible
    if (footerVisible) {
      this.elements.floatingCta.classList.remove('is-visible');
      this.elements.floatingCta.classList.add('is-near-waitlist');
    } else if (waitlistPassed) {
      this.elements.floatingCta.classList.add('is-visible');
      this.elements.floatingCta.classList.remove('is-near-waitlist');
    } else {
      this.elements.floatingCta.classList.remove('is-visible');
      this.elements.floatingCta.classList.add('is-near-waitlist');
    }
  };
  
  window.addEventListener('scroll', checkVisibility, { passive: true });
  checkVisibility();
},
  
  dispatchColorEvent(color) {
    const event = new CustomEvent('m64ColorSelected', {
      detail: { color: color },
      bubbles: true
    });
    document.dispatchEvent(event);
    
    document.documentElement.style.setProperty('--m64-selected-color', color);
    
    // Add color class to body
    document.body.classList.remove('m64-color-1', 'm64-color-2', 'm64-color-3', 'm64-color-4');
    document.body.classList.add(`m64-color-${color}`);
    
    const colorPaths = {
      '1': '/pages/m64-purple',
      '2': '/pages/m64-green',
      '3': '/pages/m64-white',
      '4': '/pages/m64-red'
    };
    window.history.replaceState({}, '', colorPaths[color]);
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => M64Reveal.init());
} else {
  M64Reveal.init();
}

// Expose for debugging
window.M64Reveal = M64Reveal;